import { Injectable, OnDestroy } from '@angular/core';
import {
    DEFAULT_EXECUTION_PAGE_SIZE,
    FrontdoorV3ExecutionService,
    isExecutionInFlight,
    LoggerService,
    MenuService,
} from '@netfoundry-ui/shared/services';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { FrontdoorSelectionService } from './frontdoor-selection.service';

/** The navigator's app code for the FD sub-app, as `FD_NAVIGATOR` declares it. */
const FD_APP = 'fd';

/** How often the poll asks. Slower than a listing, because it reads one row and drives one icon. */
const POLL_SECONDS = 30;


/**
 * Whether the selected frontdoor has an execution in flight, polled for as long as the user is
 * anywhere in the FD sub-app.
 *
 * Its own service rather than state on a page: the indicator sits in the navigator, which outlives
 * every FD page, and an execution started on one page has to show on the others.
 */
@Injectable({ providedIn: 'root' })
export class FrontdoorExecutionActivityService implements OnDestroy {
    private readonly runningSource = new BehaviorSubject<boolean>(false);
    private readonly subscription = new Subscription();

    /** Held so the same function can be taken off the document again. Bound in the constructor. */
    private readonly onVisibilityChange: () => void;

    /** The frontdoor the timer is running against, or '' when nothing is polling. */
    private polledFrontdoorId = '';
    private inFrontdoor = false;
    private frontdoorId = '';
    private checking = false;
    private timer: any;

    constructor(
        private executionService: FrontdoorV3ExecutionService,
        private selection: FrontdoorSelectionService,
        private menuService: MenuService,
        private logger: LoggerService
    ) {
        this.onVisibilityChange = () => this.sync();
        this.subscription.add(
            this.menuService.appChanged.subscribe((app) => {
                this.inFrontdoor = app === FD_APP;
                this.sync();
            })
        );
        this.subscription.add(
            this.selection.current$.subscribe((frontdoor) => {
                this.frontdoorId = frontdoor?.id ?? '';
                this.sync();
            })
        );
        document.addEventListener('visibilitychange', this.onVisibilityChange);
    }

    /** True while at least one execution of the selected frontdoor is still running. */
    get running$(): Observable<boolean> {
        return this.runningSource.asObservable();
    }

    get running(): boolean {
        return this.runningSource.value;
    }

    /** Ask now, outside the timer. For a page that just started or cancelled something. */
    refresh() {
        if (this.polledFrontdoorId) {
            this.check();
        }
    }

    ngOnDestroy() {
        this.subscription.unsubscribe();
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
        this.stop();
    }

    /* Internals -------------------------------------------------------------- */

    /**
     * Start, stop, or re-point the timer to match the current state. A hidden tab polls nothing —
     * this runs on every FD page, so a backgrounded console would otherwise ask all afternoon.
     */
    private sync() {
        const wanted = this.inFrontdoor && this.frontdoorId && !document.hidden ? this.frontdoorId : '';
        if (wanted === this.polledFrontdoorId) {
            return;
        }
        this.stop();
        if (!wanted) {
            return;
        }
        this.polledFrontdoorId = wanted;
        this.check();
        this.timer = setInterval(() => this.check(), POLL_SECONDS * 1000);
    }

    /**
     * Read the newest page and look at the statuses here, which is what the listing does. Asking
     * the service to filter by status would make the indicator depend on a second code path, and
     * a filter that answered differently would leave a spinning row under a still icon.
     *
     * The cost is that a running execution with a whole page of newer ones above it goes unseen.
     * Sorted newest first, that needs 30 starts after it, by which time it is not what the reader
     * is waiting on.
     */
    private check() {
        if (this.checking) {
            return;
        }
        const frontdoorId = this.polledFrontdoorId;
        this.checking = true;
        this.executionService
            .findExecutions(frontdoorId, 'startedAt,DESC', {}, DEFAULT_EXECUTION_PAGE_SIZE)
            .subscribe({
                next: (page) => {
                    this.checking = false;
                    const running = page.executions.some((execution) => isExecutionInFlight(execution.status));
                    this.logger.debug('frontdoor execution activity', { running, rows: page.executions.length });
                    // The answer only speaks for the frontdoor still being polled.
                    if (frontdoorId === this.polledFrontdoorId) {
                        this.publish(running);
                    }
                },
                // Logged rather than swallowed: the indicator holds its last answer either way, so
                // a poll that fails every time would otherwise look exactly like a quiet frontdoor.
                error: (error) => {
                    this.checking = false;
                    this.logger.error('frontdoor execution activity: poll failed', error);
                },
            });
    }

    /** Stop asking, and say nothing is running — the last answer was about a frontdoor we left. */
    private stop() {
        if (this.timer) {
            clearInterval(this.timer);
            this.timer = undefined;
        }
        this.polledFrontdoorId = '';
        this.checking = false;
        this.publish(false);
    }

    private publish(running: boolean) {
        if (this.runningSource.value !== running) {
            this.runningSource.next(running);
        }
    }
}
